import { useEffect, Reducer, useReducer } from 'react';
// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { formShape } from 'rc-form';
import { PaperworkFieldName, COPY_PATIENT_FIELDS_TO_GUARANTOR } from '../paperworkConstants';
import { PaymentPageNumber } from '../constants';

enum GuarantorRelationshipAction {
  Initialize,
  Touch,
  ToggleGuarantorFieldsAutoPopulated,
  Reset,
}

type Actions =
  | { type: GuarantorRelationshipAction.Initialize; payload: boolean }
  | { type: GuarantorRelationshipAction.Touch; payload: boolean }
  | { type: GuarantorRelationshipAction.ToggleGuarantorFieldsAutoPopulated; payload: boolean }
  | { type: GuarantorRelationshipAction.Reset };

type State = {
  /**
   * Used to keep track of when the form is initialized with the initial guardian relationship value
   */
  isGuarantorRelationshipInitialized: boolean;

  /**
   * Used to keep track of when guarantor relationship field has been touched
   */
  isGuarantorRelationshipFieldTouched: boolean;

  /**
   * Used to indicate if guarantor fields have already been autopopulated, so as to not overwrite them
   * if Mary selects "Self" > they autopopulate > and she edits one of the fields
   */
  hasAutopopulatedGuarantorFields: boolean;
};

const initialState: State = {
  isGuarantorRelationshipInitialized: false,
  isGuarantorRelationshipFieldTouched: false,
  hasAutopopulatedGuarantorFields: false,
};

const reducer: Reducer<State, Actions> = (state, action) => {
  switch (action.type) {
    case GuarantorRelationshipAction.Initialize:
      return { ...state, isGuarantorRelationshipInitialized: action.payload };
    case GuarantorRelationshipAction.Touch:
      return { ...state, isGuarantorRelationshipFieldTouched: action.payload };
    case GuarantorRelationshipAction.ToggleGuarantorFieldsAutoPopulated:
      return { ...state, hasAutopopulatedGuarantorFields: action.payload };
    case GuarantorRelationshipAction.Reset:
      return {
        ...state,
        isGuarantorRelationshipInitialized: false,
        isGuarantorRelationshipFieldTouched: false,
        hasAutopopulatedGuarantorFields: false,
      };
    default:
      // @ts-ignore
      throw new Error(`Unhandled action: ${action.type}`);
  }
};

const useGuarantorReducer = () => useReducer(reducer, initialState);

export const useAutoPopulateGuarantorFields = (
  formRef: React.MutableRefObject<formShape>,
  paperwork: any
) => {
  const [state, dispatch] = useGuarantorReducer();

  const guarantorRelationshipFromForm = formRef.current.getFieldValue(
    PaperworkFieldName.GUARANTOR_RELATIONSHIP
  );
  const guarantorRelationshipFromState =
    paperwork.formData[PaperworkFieldName.GUARANTOR_RELATIONSHIP];

  useEffect(() => {
    if (paperwork.pageView !== PaymentPageNumber.GuarantorInfo) {
      dispatch({ type: GuarantorRelationshipAction.Reset });
      return;
    }

    /**
     * Set guarantor relationship field as initialized once the value on the form matches the initial
     * autofill value that comes from state
     */
    if (
      guarantorRelationshipFromForm === guarantorRelationshipFromState &&
      !state.isGuarantorRelationshipInitialized
    ) {
      dispatch({ type: GuarantorRelationshipAction.Initialize, payload: true });
    }

    /**
     * Set guarantor relationship field as touched once it's been initialized and the current form value
     * doesn't match what's in state
     */
    if (
      guarantorRelationshipFromForm !== guarantorRelationshipFromState &&
      state.isGuarantorRelationshipInitialized &&
      !state.isGuarantorRelationshipFieldTouched
    ) {
      dispatch({ type: GuarantorRelationshipAction.Touch, payload: true });
    }

    if (guarantorRelationshipFromForm !== 'Self') {
      dispatch({
        type: GuarantorRelationshipAction.ToggleGuarantorFieldsAutoPopulated,
        payload: false,
      });
    }
  }, [
    guarantorRelationshipFromForm,
    guarantorRelationshipFromState,
    state.isGuarantorRelationshipInitialized,
    state.isGuarantorRelationshipFieldTouched,
    dispatch,
    paperwork.pageView,
  ]);

  useEffect(() => {
    /**
     * Only want to copy fields over if "Self" is selected and the field is touched, not by default when landing on the
     * guarantor page. Doing it by default would cause it to overwrite whatever is autofilled, if "Self" is selected, and
     * those answers should take precedence
     */
    if (
      guarantorRelationshipFromForm === 'Self' &&
      !state.hasAutopopulatedGuarantorFields &&
      state.isGuarantorRelationshipFieldTouched
    ) {
      const formFieldNames = Object.keys(formRef.current.getFieldsValue());
      const guarantorFields = {} as { [fieldName: string]: string };
      for (const [guarantorFieldName, patientFieldName] of Object.entries(
        COPY_PATIENT_FIELDS_TO_GUARANTOR
      )) {
        if (formFieldNames.includes(guarantorFieldName)) {
          guarantorFields[guarantorFieldName] = paperwork.formData[patientFieldName] ?? '';
        }
      }
      formRef.current.setFieldsValue(guarantorFields);
      dispatch({
        type: GuarantorRelationshipAction.ToggleGuarantorFieldsAutoPopulated,
        payload: true,
      });
    }
  }, [
    dispatch,
    formRef,
    paperwork.formData,
    guarantorRelationshipFromForm,
    state.hasAutopopulatedGuarantorFields,
    state.isGuarantorRelationshipFieldTouched,
  ]);
};
